import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { Surface, PageSection } from "@/components/shared";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Search, Plus, Pencil, Trash2, Warehouse as WarehouseIcon } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", code: "", address: "", manager: "", phone: "", notes: "" };

export default function Warehouses() {
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/production/warehouses");
      setItems(r.data || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load warehouses");
    }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(EMPTY); setDialogOpen(true); };
  const openEdit = (w) => {
    setEditing(w);
    setForm({ name: w.name || "", code: w.code || "", address: w.address || "", manager: w.manager || "", phone: w.phone || "", notes: w.notes || "" });
    setDialogOpen(true);
  };
  const remove = async (w) => {
    if (!window.confirm(`Delete warehouse "${w.name}"?`)) return;
    try { await api.delete(`/production/warehouses/${w.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Cannot delete"); }
  };
  const save = async () => {
    if (!form.name.trim() || !form.code.trim()) return toast.error("Name and Code are required");
    try {
      setSaving(true);
      const payload = { ...form, name: form.name.trim(), code: form.code.trim().toUpperCase() };
      if (editing) await api.put(`/production/warehouses/${editing.id}`, payload);
      else await api.post("/production/warehouses", payload);
      toast.success("Saved");
      setDialogOpen(false);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const filtered = items.filter((w) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (w.name || "").toLowerCase().includes(s) || (w.code || "").toLowerCase().includes(s) || (w.manager || "").toLowerCase().includes(s);
  });

  return (
    <div className="space-y-4">
      <PageSection title="Warehouses" description="Locations that hold finished (post-QC) inventory" action={
        <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D] gap-1.5" onClick={openNew} data-testid="warehouse-new-btn">
          <Plus className="h-4 w-4" /> New Warehouse
        </Button>
      }>
        <Surface padded={false}>
          <div className="p-3 border-b border-gray-100 flex items-center gap-3">
            <div className="relative flex-1 max-w-sm">
              <Search className="h-4 w-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" />
              <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search name, code, manager..." className="pl-8" data-testid="warehouse-search" />
            </div>
            <span className="text-xs text-gray-500 ml-auto">{filtered.length} warehouse(s)</span>
          </div>
          <table className="w-full text-sm">
            <thead className="bg-gray-50">
              <tr className="text-left text-[11px] uppercase tracking-widest text-gray-500">
                <th className="px-3 py-2">Code</th>
                <th className="px-3 py-2">Name</th>
                <th className="px-3 py-2">Manager</th>
                <th className="px-3 py-2">Phone</th>
                <th className="px-3 py-2">Address</th>
                <th className="px-3 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="px-3 py-10 text-center text-gray-400">
                  <WarehouseIcon className="h-8 w-8 mx-auto mb-2 opacity-30" />
                  No warehouses yet. Click <span className="font-semibold">New Warehouse</span>.
                </td></tr>
              )}
              {filtered.map((w) => (
                <tr key={w.id} className="hover:bg-gray-50" data-testid={`warehouse-row-${w.id}`}>
                  <td className="px-3 py-2 font-mono text-xs font-semibold">{w.code}</td>
                  <td className="px-3 py-2 font-medium">{w.name}</td>
                  <td className="px-3 py-2 text-gray-600">{w.manager || "—"}</td>
                  <td className="px-3 py-2 text-gray-600 font-mono text-xs">{w.phone || "—"}</td>
                  <td className="px-3 py-2 text-gray-500 max-w-xs truncate">{w.address || "—"}</td>
                  <td className="px-3 py-2 text-right">
                    <button className="h-7 w-7 rounded hover:bg-gray-100 inline-flex items-center justify-center" onClick={() => openEdit(w)} data-testid={`warehouse-edit-${w.id}`}>
                      <Pencil className="h-3.5 w-3.5 text-gray-500" />
                    </button>
                    <button className="h-7 w-7 rounded hover:bg-red-50 inline-flex items-center justify-center ml-1" onClick={() => remove(w)} data-testid={`warehouse-delete-${w.id}`}>
                      <Trash2 className="h-3.5 w-3.5 text-red-500" />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Surface>
      </PageSection>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="warehouse-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit Warehouse" : "New Warehouse"}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <F label="Name *" className="col-span-2"><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="warehouse-name" /></F>
            <F label="Code *"><Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="font-mono" data-testid="warehouse-code" /></F>
            <F label="Phone"><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="warehouse-phone" /></F>
            <F label="Manager" className="col-span-2"><Input value={form.manager} onChange={(e) => setForm({ ...form, manager: e.target.value })} data-testid="warehouse-manager" /></F>
            <F label="Address" className="col-span-2"><Input value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value })} data-testid="warehouse-address" /></F>
            <F label="Notes" className="col-span-2"><Input value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="warehouse-notes" /></F>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
            <Button className="bg-[#9B1C1C] hover:bg-[#7F1D1D]" onClick={save} disabled={saving} data-testid="warehouse-save">{saving ? "Saving…" : "Save"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function F({ label, className = "", children }) {
  return <label className={`text-sm ${className}`}><span className="block text-[11px] uppercase tracking-widest text-gray-500 mb-1">{label}</span>{children}</label>;
}
